// eslint-disable-next-line jira/restricted/react-component-props
import React, { useContext, type ReactElement, type ComponentProps, type ReactNode } from 'react';
import { styled as styled2 } from '@compiled/react';
// eslint-disable-next-line jira/restricted/styled-components-migration, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from 'styled-components';
import isEmpty from 'lodash/isEmpty';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
// @ts-expect-error - TS2614 - Module '"@atlaskit/dynamic-table"' has no exported member 'HeadType'. Did you mean to use 'import HeadType from "@atlaskit/dynamic-table"' instead? | TS2614 - Module '"@atlaskit/dynamic-table"' has no exported member 'RowType'. Did you mean to use 'import RowType from "@atlaskit/dynamic-table"' instead?
import { DynamicTableStateless, type HeadType, type RowType } from '@atlaskit/dynamic-table';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import { fireUIAnalytics, fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { Application } from '@atlassian/jira-shared-types/src/application.tsx';
import type { FormatMessage } from '@atlassian/jira-shared-types/src/general.tsx';
import { TRACK_EVENT_ACTION } from '../../common/constants';
import type { Actor } from '../../common/types';
import { MAX_RESULTS_PER_PAGE } from '../../controllers/table-controller/constants';
import type { RemoveActorProps } from '../../controllers/table-controller/types';
import MainProviderContext from '../../services/access-page-context';
import RemoveActorServiceDI from '../../services/remove-actor';
import { ANALYTICS_SORT_TABLE } from './constants';
import IntroView from './intro-view';
import messages from './messages';
import Pagination from './pagination';
import PermissionGateView from './permission-gate-view';
import { getApplicationName } from './permission-gate-view/utils';
import RemoveActorDialog from './remove-actor-dialog';
import { ANALYTICS_REMOVE_BTN } from './remove-actor-dialog/constants';
import ActorItemSkeleton from './skeleton';
import TableEmptyView from './table-empty-view';
import TableErrorView from './table-error-view';
import TableFilterEmptyView from './table-filter-empty-view';
import type { DumbTableProps, EmptyViewComponent, SortData } from './types';
import { createActorRowType, isNameSortable } from './utils';

const renderEmptyView = (
	isFetching: boolean,
	isError: boolean,
	isThereActorInProject: boolean,
	application: Application,
	hasPermissionsGate: boolean,
	onRefresh: () => void,
	isProjectSimplified: boolean,
): EmptyViewComponent => {
	if (isError) {
		return <TableErrorView onRefresh={onRefresh} />;
	}
	if (isThereActorInProject) {
		return <TableFilterEmptyView />;
	}

	if (hasPermissionsGate && !isProjectSimplified) {
		return <PermissionGateView application={application} />;
	}

	if (!isFetching) {
		return <TableEmptyView />;
	}
	return <></>;
};

const renderIntroView = (
	shouldDisplayIntro: boolean,
	hasPermissionsGate: boolean,
	isProjectSimplified: boolean,
	application: Application,
): ReactElement<ComponentProps<typeof PermissionGateView | typeof IntroView>> | null => {
	if (shouldDisplayIntro) {
		return hasPermissionsGate && !isProjectSimplified ? (
			<PermissionGateView application={application} />
		) : (
			<IntroView />
		);
	}
	return null;
};

const renderRemoveActorService = (
	{
		isOpen,
		currentActor,
		onActorRemovedSuccess,
		onActorRemovedFailure,
		onDismiss,
	}: RemoveActorProps,
	// @ts-expect-error - TS2304 - Cannot find name 'ComponentType'. | TS2304 - Cannot find name 'Props'.
	RemoveActorService: ComponentType<Props>,
) => (
	<RemoveActorService
		onActorRemovedSuccess={onActorRemovedSuccess}
		onActorRemovedFailure={onActorRemovedFailure}
	>
		{/* @ts-expect-error - TS7031 - Binding element 'loading' implicitly has an 'any' type. | TS7031 - Binding element 'onRemoveActor' implicitly has an 'any' type. */}
		{({ loading, onRemoveActor }) =>
			isOpen ? (
				<RemoveActorDialog
					hasPermissionsGate={false}
					actor={currentActor}
					isSubmitting={loading}
					onDismiss={onDismiss}
					onConfirm={(actor, event: UIAnalyticsEvent) =>
						onRemoveActor(actor).then(() => {
							fireTrackAnalytics(event, TRACK_EVENT_ACTION, ANALYTICS_REMOVE_BTN, {});
						})
					}
				/>
			) : null
		}
	</RemoveActorService>
);

// @ts-expect-error - TS7031 - Binding element 'formatMessage' implicitly has an 'any' type.
const renderActors = (actors: Actor[], { formatMessage, ...otherProps }): RowType[] =>
	actors.map(
		(actor: Actor, index: number): RowType =>
			// @ts-expect-error - TS2345 - Argument of type '{ actor: Actor; index: number; removeButtonText: any; }' is not assignable to parameter of type 'CreateActorRowParams'.
			createActorRowType({
				...otherProps,
				actor,
				index,
				removeButtonText: formatMessage(messages.remove),
			}),
	);

const getHead = (
	formatMessage: FormatMessage,
	pageNumber: number,
	isLastPage: boolean,
	actorsLength: number,
): HeadType => {
	const head: HeadType = {
		cells: [
			{
				key: 'NAME',
				isSortable: isNameSortable(pageNumber, isLastPage, actorsLength),
				content: formatMessage(messages.nameColumnHeader),
				width: 35,
				testId: 'users-and-roles.ui.actors-table.table.NAME',
			},
			{
				key: 'EMAIL',
				isSortable: false,
				content: formatMessage(messages.emailColumnHeader),
				width: 35,
				testId: 'users-and-roles.ui.actors-table.table.EMAIL',
			},
			{
				key: 'ROLES',
				isSortable: false,
				content: formatMessage(messages.roleColumnHeader),
				width: 20,
				testId: 'users-and-roles.ui.actors-table.table.ROLES',
			},
			{
				key: 'REMOVE',
				isSortable: false,
				content: formatMessage(messages.removeColumnHeader),
				width: 10,
				testId: 'users-and-roles.ui.actors-table.table.REMOVE',
			},
		],
	};
	return head;
};

export const ActorsTable = (props: DumbTableProps) => {
	const {
		application,
		hasPermissionsGate,
		intl: { formatMessage },
		isError,
		isFetching,
		isLastPage,
		isThereActorInProject,
		numberOfPages = 0,
		onPageChange,
		onActorRemoval,
		onRefresh,
		onSort,
		page,
		actors,
		pageNumber,
		projectRoles,
		shouldDisplayIntro,
		sortKey: key,
		sortOrder: order,
		removeActor,
		onRolesUpdated,
		shouldShowSkeleton,
		RemoveActorService,
	} = props;

	const onClickSort = (sortData: SortData, analyticsEvent?: UIAnalyticsEvent): void => {
		// @ts-expect-error - Argument of type 'UIAnalyticsEvent | undefined' is not assignable to parameter of type 'UIAnalyticsEvent'.
		fireUIAnalytics(analyticsEvent, ANALYTICS_SORT_TABLE);
		onSort(sortData);
	};
	const isPaginationVisible = numberOfPages > 1;
	const createRowProps = {
		hasPermissionsGate,
		onActorRemoval,
		projectRoles,
		onSelectActorToRemove: removeActor.onSelectActorToRemove,
		formatMessage,
		onRolesUpdated,
		applicationName: getApplicationName(application),
	};
	const tableRows: RowType[] = renderActors(actors, createRowProps);

	const isRemoveActorNotEmpty = !isEmpty(removeActor);
	const pageContext = useContext(MainProviderContext);
	const { isProjectSimplified } = pageContext;
	const emptyView = renderEmptyView(
		isFetching,
		isError,
		isThereActorInProject,
		application,
		hasPermissionsGate,
		onRefresh,
		isProjectSimplified,
	);

	const head: HeadType = getHead(formatMessage, pageNumber, isLastPage, actors.length);

	const paginationi18n = {
		prev: formatMessage(messages.paginationPrev),
		next: formatMessage(messages.paginationNext),
	};

	return (
		<TableWrapper data-pollinator-people-table>
			<Table data-testid="users-and-roles.ui.actors-table.table-wrapper">
				{
					// MC-720 The table here is used for pollinator synthetic checks in
					// https://stash.atlassian.com/projects/JSRE/repos/pollinator-synthetic-checks
					// The 'tbody' is what is looked for, which prevents the check from passing
					// unless the table content actually loads.
					// Check: PeoplePageAgility, PeoplePageScrum
				}

				{shouldShowSkeleton ? (
					<ActorItemSkeleton rows={page === 1 ? 3 : MAX_RESULTS_PER_PAGE} />
				) : (
					<div data-testid="users-and-roles.ui.actors-table.table">
						<DynamicTableStateless
							isFixedSize
							isLoading={isFetching}
							head={head}
							rows={tableRows}
							isRankable={false}
							onSort={onClickSort}
							testId="users-and-roles.ui.actors-table.dynamic-table"
							sortKey={key}
							sortOrder={order}
							emptyView={emptyView}
							// @ts-expect-error - TS2741 - Property 'label' is missing in type '{ prev: string; next: string; }' but required in type 'I18nShape'.
							paginationi18n={paginationi18n}
							label={formatMessage(messages.tableAriaLabel)}
						/>
					</div>
				)}
			</Table>

			{isPaginationVisible && !shouldShowSkeleton && (
				<PaginationWrapper>
					<Pagination
						isDisabled={isFetching}
						pages={isLastPage ? numberOfPages : numberOfPages + 1}
						selectedIndex={page - 1}
						onChange={(newIndex: number) => onPageChange(newIndex + 1)}
					/>
				</PaginationWrapper>
			)}
			{renderIntroView(shouldDisplayIntro, hasPermissionsGate, isProjectSimplified, application)}
			{isRemoveActorNotEmpty ? renderRemoveActorService(removeActor, RemoveActorService) : null}
		</TableWrapper>
	);
};

ActorsTable.defaultProps = {
	numberOfPages: 0,
	RemoveActorService: RemoveActorServiceDI,
};

export default injectIntl(ActorsTable);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Table = styled2.div<{
	children?: ReactNode;
	['data-testid']?: string;
}>({
	borderCollapse: 'collapse',
	width: '100%',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginBottom: '10px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableWrapper = styled.div<{
	children?: ReactNode;
	['data-pollinator-people-table']?: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	table: {
		tableLayout: 'fixed',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PaginationWrapper = styled2.div<{
	children?: ReactNode;
}>({
	display: 'flex',
	justifyContent: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	margin: `${token('space.200', '16px')} 0 ${gridSize * 7}px 0`,
});
